<template>
  <form
    class="event-filter"
    role="search"
    autocomplete="off"
    @submit.prevent
  >
    <div class="form-group" data-tour="text">
      <input
        id="event-search-text"
        type="search"
        name="event-search-text"
        class="form-control"
        autocomplete="off"
        v-model="textSearch"
        @input="handleTextInput"
        :placeholder="copy.filter.textSearchPlaceholder"
      >
    </div>

    <div data-tour="location">
      <div class="form-group">
        <label class="form-label" for="event-search-country">{{ copy.filter.country }}</label>
        <select
          id="event-search-country"
          name="event-search-country"
          class="form-control"
          autocomplete="off"
          v-model="selectedCountry"
          @change="handleCountryChange"
        >
          <option
            v-for="option in formData.countryOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </div>

      <div class="form-group">
        <label class="form-label" for="event-search-region">{{ copy.filter.region }}</label>
        <select
          id="event-search-region"
          name="event-search-region"
          class="form-control"
          autocomplete="off"
          v-model="selectedRegion"
          @change="emitFilterChange"
          :disabled="!selectedCountry"
        >
          <option value="">{{ selectedCountry ? copy.filter.allRegions : '' }}</option>
          <option
            v-for="option in regionOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </div>
    </div>

    <div class="form-group" data-tour="distance">
      <label class="form-label">
        {{ copy.filter.distance }}
        ({{ distanceRange[0] }} - {{ distanceRange[1] }}{{ maxDistanceIndicator }} {{ distanceUnits }})
      </label>
      <hd-range-slider
        ref="distanceSlider"
        v-model="distanceRange"
        :max="100"
        :min="1"
        :min-range="1"
        :interval="1"
      ></hd-range-slider>
    </div>

    <div data-tour="race-type">
      <div class="form-group">
        <label class="form-label" for="event-search-activity-type">{{ copy.filter.activityType }}</label>
        <select
          id="event-search-activity-type"
          name="event-search-activity-type"
          class="form-control"
          autocomplete="off"
          v-model="selectedActivityType"
          @change="handleActivityTypeChange"
        >
          <option value="">{{ copy.filter.allActivityTypes }}</option>
          <option
            v-for="option in formData.activityTypes"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </div>

      <div class="form-group">
        <label class="form-label" for="event-search-terrain">{{ copy.filter.terrain }}</label>
        <select
          id="event-search-terrain"
          name="event-search-terrain"
          class="form-control"
          autocomplete="off"
          v-model="selectedTerrain"
          @change="emitFilterChange"
          :disabled="terrainDisabled"
          :aria-describedby="terrainDisabled ? 'event-search-terrain-note' : null"
        >
          <option value="">{{ copy.filter.allTerrain }}</option>
          <option
            v-for="option in formData.terrainTypes"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
        <small
          v-if="terrainDisabled"
          id="event-search-terrain-note"
          class="form-text text-muted"
        >
          {{ copy.filter.terrainSwimNote }}
        </small>
      </div>
    </div>

    <div data-tour="dates">
      <h6 class="mt-4 mb-3">{{ copy.filter.dateRange }}</h6>

      <div class="row">
        <div class="col-6">
          <div class="form-group">
            <label class="form-label" for="event-search-start-date">{{ copy.filter.after }}</label>
            <input
              id="event-search-start-date"
              name="event-search-start-date"
              type="date"
              class="form-control"
              autocomplete="off"
              v-model="startDate"
              @change="emitFilterChange"
            >
          </div>
        </div>
        <div class="col-6">
          <div class="form-group">
            <label class="form-label" for="event-search-end-date">{{ copy.filter.before }}</label>
            <input
              id="event-search-end-date"
              name="event-search-end-date"
              type="date"
              class="form-control"
              autocomplete="off"
              v-model="endDate"
              @change="emitFilterChange"
            >
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import axios from 'axios';
import _ from 'lodash';
import { HdRangeSlider } from '../../ui';
import { terrainDisabledFor, terrainFor } from '../../utilities/event_filters';

export default {
  props: ['formData', 'useMetric', 'copy'],

  data() {
    return {
      textSearch: this.formData.initialText || '',
      selectedCountry: this.formData.initialCountry,
      selectedRegion: this.formData.initialRegion || '',
      selectedActivityType: this.formData.initialActivityType || '',
      selectedTerrain: this.initialTerrain(),
      distanceRange: this.initializeDistanceRange(),
      startDate: this.formData.startDate,
      endDate: this.formData.endDate,
      regionOptions: this.formData.regionOptions || []
    };
  },

  mounted() {
    // If country is selected on mount, fetch regions
    if (this.selectedCountry) {
      this.fetchRegions();
    }
  },

  components: { HdRangeSlider },

  watch: {
    distanceRange: {
      handler() {
        this.emitFilterChange();
      },
      deep: true
    }
  },

  computed: {
    distanceUnits() {
      return this.useMetric ? 'km' : 'mi';
    },

    maxDistanceIndicator() {
      return this.distanceRange[1] >= 100 ? '+' : '';
    },

    terrainDisabled() {
      return terrainDisabledFor(this.selectedActivityType);
    }
  },

  methods: {
    initialTerrain() {
      return terrainFor(this.formData.initialActivityType, this.formData.initialTerrain) || '';
    },

    initializeDistanceRange() {
      // If URL provided initial distance values, use them
      if (this.formData.initialDistance) {
        return [
          this.formData.initialDistance.min || 1,
          this.formData.initialDistance.max || 100
        ];
      }
      return [1, 100];
    },

    handleTextInput() {
      // Only trigger search if 2+ characters or empty (to clear)
      if (this.textSearch.length >= 2 || this.textSearch.length === 0) {
        this.debouncedEmitFilterChange();
      }
    },

    debouncedEmitFilterChange: _.debounce(function() {
      this.emitFilterChange();
    }, 400),

    handleActivityTypeChange() {
      // Drop any terrain the searcher had picked before switching to swim, so
      // the locked-out select can't keep narrowing the results invisibly.
      if (this.terrainDisabled) {
        this.selectedTerrain = '';
      }
      this.emitFilterChange();
    },

    handleCountryChange() {
      this.selectedRegion = '';
      if (this.selectedCountry) {
        this.fetchRegions();
      } else {
        this.regionOptions = [];
      }
      this.emitFilterChange();
    },

    fetchRegions() {
      if (!this.selectedCountry) {
        this.regionOptions = [];
        return;
      }
      const self = this;
      axios.get(`/geography/countries/${this.selectedCountry}/regions`, {
        headers: { 'Accept': 'application/json' }
      }).then(response => {
        self.regionOptions = response.data.regions.map(region => ({
          value: region.value,
          label: region.name
        }));
      }).catch(error => {
        console.error('Error fetching regions:', error);
        self.regionOptions = [];
      });
    },

    emitFilterChange() {
      const conversionRate = this.useMetric ? 1000 : 1609;

      this.$emit('filter-change', {
        text: this.textSearch || null,
        country: this.selectedCountry,
        region: this.selectedRegion,
        activity_type: this.selectedActivityType || null,
        terrain: terrainFor(this.selectedActivityType, this.selectedTerrain),
        distance: {
          min: this.distanceRange[0] * conversionRate,
          max: this.distanceRange[1] * conversionRate,
          min_reached: this.distanceRange[0] <= 1,
          max_reached: this.distanceRange[1] >= 100
        },
        date_range: {
          start: this.startDate,
          end: this.endDate
        }
      });
    }
  },

};
</script>

<style scoped>
.event-filter .form-label {
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.25rem;
}

.event-filter .form-group {
  margin-bottom: 1rem;
}
</style>
